import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePdf } from '@mikecousins/react-pdf';
import PdfImageConverter from './PdfImageConverter';
import LoadingIndicator from './LoadingIndicator';
import { dataURLtoBlob } from '../utils/file';
import _ from 'lodash';
import ImageSlideViewer from './ImageSlideViewer';

export default function PdfImagePreview({ url, name, onDocumentLoad, onPageChange, onError }) {
  const canvasRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState([]);
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(-1);

  const { pdfDocument } = usePdf({
    file: url,
    canvasRef,
    page: 1,
  });

  useEffect(() => {
    if (!pdfDocument) {
      return;
    }
    setLoadedImages(Array(pdfDocument.numPages).fill(null));
    if (pdfDocument.numPages) {
      setCurrentLoadingIndex(0);
    }
  }, [pdfDocument]);

  useEffect(() => {
    // Revoke any object URLs
    return () => {
      loadedImages.forEach((blobUrl) => !!blobUrl && URL.revokeObjectURL(blobUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLoadedPages = useCallback(
    (index, imageDataUrl) => {
      try {
        setLoadedImages(
          loadedImages.map((li, i) => (i === index ? URL.createObjectURL(dataURLtoBlob(imageDataUrl)) : li))
        );
      } catch (e) {
        console.error(e);
        onError();
      }
    },
    [loadedImages, onError]
  );

  useEffect(() => {
    if (currentLoadingIndex !== -1 && loadedImages[currentLoadingIndex]) {
      const nextIndex =
        currentLoadingIndex < loadedImages.length - 1
          ? currentLoadingIndex + 1
          : _.findLastIndex(loadedImages, _.negate(Boolean));
      if (nextIndex !== -1) {
        setCurrentLoadingIndex(nextIndex);
      } else {
        setCurrentLoadingIndex(-1);
      }
    }
  }, [loadedImages, currentLoadingIndex]);

  return (
    <>
      {currentLoadingIndex !== -1 && (
        <PdfImageConverter
          url={url}
          page={currentLoadingIndex + 1}
          onImageReady={(imageBlob) => updateLoadedPages(currentLoadingIndex, imageBlob)}
        />
      )}
      {loadedImages.some(Boolean) ? (
        <ImageSlideViewer
          name={name}
          url={url}
          imageSources={loadedImages}
          onDocumentLoad={onDocumentLoad}
          onPageChange={onPageChange}
        />
      ) : (
        <div className='flex w-full h-full justify-center items-center'>
          <LoadingIndicator />
        </div>
      )}
    </>
  );
}
